import { SCREEN_NAMES } from '@/lib/analytics/consts.ts'
import { useAnalytics } from '@/composables/analytics'
import { useRouter } from 'vue-router'
import type { Ref } from 'vue'
import { ref } from 'vue'

type ScreenName = string | null
type RouteStrategyFn = (currentScreenName: ScreenName) => ScreenName

interface LoyaltyPropertiesContext {
  loyaltyCardsActivated?: number
  clickSource?: string
  loyaltyProgramId?: string
}

interface SendEventParams {
  eventAction: string
  screenName?: string
}

export interface UseLoyaltyAnalyticsResult {
  sendEvent: (params: SendEventParams) => void
  updatePropertiesContext: (context: Partial<LoyaltyPropertiesContext>) => void
  resetLoyaltyAnalyticsState: () => void
}

const lastStateScreenName: Ref<ScreenName> = ref(null)
const propertiesContext: Ref<LoyaltyPropertiesContext> = ref({})

const routeStrategies: Record<string, RouteStrategyFn> = {
  'settings.loyalty-program': (currentScreenName) => {
    return currentScreenName !== SCREEN_NAMES.LP_MAIN_SCREEN
      ? SCREEN_NAMES.LP_ACTIVATION
      : currentScreenName
  },
  'settings.loyalty-program.program': (currentScreenName) => currentScreenName,
  'settings.loyalty-program.cards.list': (currentScreenName) => {
    return currentScreenName !== SCREEN_NAMES.LC_CARDS
      ? SCREEN_NAMES.LC_MANAGE
      : currentScreenName
  },
  default: () => null,
}

const getRouteStrategy = (routeName: string | symbol | undefined): RouteStrategyFn => {
  const key = typeof routeName === 'string' ? routeName : 'default'
  return routeStrategies[key] ?? routeStrategies.default
}

// Screen-specific property mappings
const screenProperties: Record<string, () => Partial<LoyaltyPropertiesContext>> = {
  [SCREEN_NAMES.LP_MAIN_SCREEN]: () => ({
    loyaltyCardsActivated: propertiesContext.value.loyaltyCardsActivated,
    clickSource: propertiesContext.value.clickSource,
  }),

  [SCREEN_NAMES.LP_SETTINGS]: () => ({
    loyaltyCardsActivated: propertiesContext.value.loyaltyCardsActivated,
    loyaltyProgramId: propertiesContext.value.loyaltyProgramId,
  }),
}

const getProperties = (): Partial<LoyaltyPropertiesContext> => {
  return screenProperties[lastStateScreenName.value ?? '']?.() ?? {}
}

const updatePropertiesContext = (context: Partial<LoyaltyPropertiesContext>): void => {
  propertiesContext.value = { ...propertiesContext.value, ...context }
}

// Reset function for testing
const resetLoyaltyAnalyticsState = (): void => {
  lastStateScreenName.value = null
  propertiesContext.value = {}
}

export const useLoyaltyAnalytics = (): UseLoyaltyAnalyticsResult => {
  const analytics = useAnalytics()
  const router = useRouter()

  const sendEvent = ({ eventAction, screenName }: SendEventParams): void => {
    if (screenName) {
      lastStateScreenName.value = screenName
    } else {
      const currentRoute = router.currentRoute.value.name
      const strategy = getRouteStrategy(currentRoute)
      lastStateScreenName.value = strategy(lastStateScreenName.value)
    }

    if (lastStateScreenName.value) {
      analytics.dispatch(analytics.EVENTS.LOYALTY_CARD_ACTION, {
        screenName: lastStateScreenName.value,
        eventAction,
        ...getProperties(),
      })
    }
  }

  return {
    sendEvent,
    updatePropertiesContext,
    resetLoyaltyAnalyticsState,
  }
}
